import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Box, Typography, Button, TextField, Tabs, Tab, Chip, Stack,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Tooltip, IconButton, Paper, Alert, CircularProgress, Divider,
} from '@mui/material'
import {
  Save as SaveIcon,
  Smartphone as MobileIcon,
  DesktopWindows as DesktopIcon,
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Business as OrgIcon,
  ArrowForward as NextIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { themeColors } from '../../app/layout/theme'
import {
  leadFormsApi, LeadFormField, CreateLeadFormPayload,
  DEFAULT_LEAD_FIELDS, CreateLeadFormFieldPayload,
} from '../../lib/api/leadFormsApi'
import { orgsApi, lobsApi } from '../../lib/api/tenantsApi'
import { useOrgSelector } from '../../context/OrgSelectorContext'
import { authStore } from '../../lib/stores/authStore'
import { FieldEditor } from './FieldEditor'
import { FormPreview } from './FormPreview'
import { LeadFormResponses } from './LeadFormResponses'

function generateId() {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function slugify(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80)
}

function buildDefaultFields(): LeadFormField[] {
  return DEFAULT_LEAD_FIELDS.map((df, idx) => ({
    id:           generateId(),
    formId:       '',
    fieldType:    df.fieldType,
    fieldName:    df.key,
    label:        df.label,
    placeholder:  '',
    required:     df.required,
    displayOrder: idx,
    mappingField: df.mappingField,
    configuration: {},
    isDefault:    true,
    isVisible:    df.visible,
  }))
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': { borderColor: themeColors.border },
    '&:hover fieldset': { borderColor: themeColors.primary },
    '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': { fontSize: '13px' },
  '& .MuiInputLabel-root': { fontSize: '13px' },
}

const selectSx = {
  ...inputSx,
  '& .MuiSelect-select': { fontSize: '13px' },
}

// ─────────────────────────────────────────────────────────────────────────────
export const LeadFormBuilder = () => {
  const { id }      = useParams<{ id?: string }>()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const isEdit      = !!id && id !== 'new'

  // ── Role-based org scoping ─────────────────────────────────────────────────
  // Admins (org-scoped users) must never see or pick another organisation —
  // their Custom Forms are always assigned to their own org. Only Super Admins
  // (cross-tenant) get the full org picker.
  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // usePermission's roleName-string match — that doesn't reliably match every
  // SuperAdmin's role label, which previously locked this dropdown for SuperAdmin too.
  const { isSuperAdmin } = useOrgSelector()
  const userOrgId   = authStore.user?.orgId   ?? ''
  const userOrgName = authStore.user?.orgName ?? ''

  // Step 1 = config panel (new forms only), Step 2 = builder
  const [step, setStep] = useState<'config' | 'builder'>(isEdit ? 'builder' : 'config')

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formName,       setFormName]       = useState('')
  const [description,    setDescription]    = useState('')
  const [orgId,          setOrgId]          = useState('')
  const [lobId,          setLobId]          = useState('')
  const [successMessage, setSuccessMessage] = useState('Thank you! We will contact you shortly.')
  const [redirectUrl,    setRedirectUrl]    = useState('')
  const [status,         setStatus]         = useState<'Active' | 'Inactive'>('Active')
  const [sourceAdName,      setSourceAdName]      = useState('')
  const [sourceCampaign,    setSourceCampaign]    = useState('')
  const [sourceMedium,      setSourceMedium]      = useState('')
  const [formCompanyName,   setFormCompanyName]   = useState('')
  const [formLogoUrl,       setFormLogoUrl]       = useState('')
  const [logoUploading,     setLogoUploading]     = useState(false)
  const [slug,           setSlug]           = useState('')
  const [slugManual,     setSlugManual]     = useState(false)
  const [fields,         setFields]         = useState<LeadFormField[]>(buildDefaultFields)
  const [previewMode,    setPreviewMode]    = useState<'desktop' | 'mobile'>('desktop')
  const [activeTab,      setActiveTab]      = useState(0)

  const dragItem = useRef<number | null>(null)
  const dragOver = useRef<number | null>(null)

  const BASE_URL = window.location.origin

  // Branded URL: /forms/{slug}/{company-slug} when company name is set,
  // otherwise just /forms/{slug}
  const companyUrlSegment = formCompanyName
    ? '/' + formCompanyName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    : ''

  // ── Tenant dropdown queries ────────────────────────────────────────────────
  // Only Super Admins need the full org list — Admins are locked to their own org.
  const { data: orgs = [] } = useQuery({
    queryKey: ['orgs'],
    queryFn:  orgsApi.getAll,
    enabled:  isSuperAdmin,
    staleTime: 5 * 60_000,
  })

  const { data: lobs = [] } = useQuery({
    queryKey: ['lobs', orgId],
    queryFn:  () => lobsApi.getAll(orgId || undefined),
    enabled:  !!orgId,
    staleTime: 5 * 60_000,
  })

  // For non-SuperAdmins the org list isn't fetched at all (locked to their own org),
  // so fall back to the org name already present on their auth profile.
  const orgName       = isSuperAdmin ? (orgs.find(o => o.id === orgId)?.name ?? '') : userOrgName
  const lobName       = lobs.find(l => l.id === lobId)?.name ?? ''
  const configComplete = !!orgId && !!lobId

  // ── Load existing form ─────────────────────────────────────────────────────
  const { data: existingForm, isLoading: loadingForm } = useQuery({
    queryKey: ['leadForm', id],
    queryFn:  () => leadFormsApi.getById(id!),
    enabled:  isEdit,
  })

  useEffect(() => {
    if (!existingForm) return
    setFormName(existingForm.name)
    setDescription(existingForm.description ?? '')
    setOrgId(existingForm.organizationId ?? '')
    setLobId(existingForm.lobId ?? '')
    setSuccessMessage(existingForm.successMessage)
    setRedirectUrl(existingForm.redirectUrl ?? '')
    setStatus(existingForm.status)
    setSourceAdName(existingForm.sourceAdName ?? '')
    setSourceCampaign(existingForm.sourceCampaign ?? '')
    setSourceMedium(existingForm.sourceMedium ?? '')
    setFormCompanyName(existingForm.formCompanyName ?? '')
    setFormLogoUrl(existingForm.formLogoUrl ?? '')
    setSlug(existingForm.slug)
    setSlugManual(true)
    const SOURCE_KEYS = ['source', 'source_ad_name', 'source_campaign', 'source_medium']
    const loadedFields = existingForm.fields
      .filter(f => !SOURCE_KEYS.includes(f.fieldName))
      .map(f => ({ ...f, configuration: f.configuration ?? {} }))

    const existingKeys = new Set(loadedFields.map(f => f.fieldName))
    const missingDefaults: LeadFormField[] = DEFAULT_LEAD_FIELDS
      .filter(df => !existingKeys.has(df.key))
      .map((df, idx) => ({
        id:           generateId(),
        formId:       existingForm.id,
        fieldType:    df.fieldType,
        fieldName:    df.key,
        label:        df.label,
        placeholder:  '',
        required:     df.required,
        displayOrder: loadedFields.length + idx,
        mappingField: df.mappingField,
        configuration: {},
        isDefault:    true,
        isVisible:    false,
      }))

    setFields([...loadedFields, ...missingDefaults])
  }, [existingForm])

  useEffect(() => {
    if (!slugManual && formName) setSlug(slugify(formName))
  }, [formName, slugManual])

  // Admins can't pick an org — lock new forms to the Admin's own org.
  // (Existing forms keep whatever org they were created with, set by the
  // existingForm effect above; the backend re-validates this on save regardless.)
  useEffect(() => {
    if (!isSuperAdmin && !isEdit && userOrgId) {
      setOrgId(userOrgId)
    }
  }, [isSuperAdmin, isEdit, userOrgId])

  const handleOrgChange = (newOrgId: string) => {
    setOrgId(newOrgId)
    setLobId('')
  }

  const handleLobChange = (newLobId: string) => {
    setLobId(newLobId)
  }

  // ── Save mutation ──────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId || !lobId) {
        throw new Error('Please select Organization and LOB before saving.')
      }
      const fieldPayloads: CreateLeadFormFieldPayload[] = fields.map((f, idx) => ({
        fieldType:     f.fieldType,
        fieldName:     f.fieldName,
        label:         f.label,
        placeholder:   f.placeholder ?? undefined,
        required:      f.required,
        displayOrder:  idx,
        mappingField:  f.mappingField ?? undefined,
        isDefault:     f.isDefault,
        isVisible:     f.isVisible,
        configuration: f.configuration ?? {},
      }))
      const payload: CreateLeadFormPayload = {
        name:           formName,
        description:    description || undefined,
        organizationId: orgId,
        lobId,
        warehouseId: null,
        sourceAdName:    sourceAdName    || undefined,
        sourceCampaign:  sourceCampaign  || undefined,
        sourceMedium:    sourceMedium    || undefined,
        formCompanyName: formCompanyName || undefined,
        formLogoUrl:  formLogoUrl  || undefined,
        successMessage,
        redirectUrl:    redirectUrl || undefined,
        status,
        fields:         fieldPayloads,
      }
      return isEdit ? leadFormsApi.update(id!, payload) : leadFormsApi.create(payload)
    },
    onSuccess: (form) => {
      toast.success(isEdit ? 'Form updated!' : 'Form created!')
      queryClient.invalidateQueries({ queryKey: ['leadForms'] })
      if (!isEdit) navigate(`/custom-forms/${form.id}/edit`)
    },
    onError: (err: any) => toast.error(err?.message ?? 'Failed to save form'),
  })

  // ── Field operations ───────────────────────────────────────────────────────
  const handleUpdateField = useCallback((fieldId: string, updates: Partial<LeadFormField>) =>
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f)), [])

  const handleDeleteField = useCallback((fieldId: string) =>
    setFields(prev => prev.filter(f => f.id !== fieldId)), [])

  const handleDragStart = (idx: number) => { dragItem.current = idx }
  const handleDragEnter = (idx: number) => { dragOver.current = idx }
  const handleDragEnd   = () => {
    if (dragItem.current === null || dragOver.current === null) return
    const reordered = [...fields]
    const [moved] = reordered.splice(dragItem.current, 1)
    reordered.splice(dragOver.current, 0, moved)
    setFields(reordered)
    dragItem.current = null
    dragOver.current = null
  }

  const copyUrl = () => {
    navigator.clipboard.writeText(`${BASE_URL}/forms/${slug}${companyUrlSegment}`)
    toast.success('Form URL copied!')
  }

  if (loadingForm) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
        <CircularProgress sx={{ color: themeColors.primary }} />
      </Box>
    )
  }

  const formUrl = `${BASE_URL}/forms/${slug}${companyUrlSegment}`

  // ── STEP 1: Org / LOB / Warehouse config ──────────────────────────────────
  if (step === 'config') {
    return (
      <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${themeColors.border}`, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => navigate('/custom-forms')} sx={{ color: themeColors.text.secondary }}>
            <BackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: themeColors.text.primary, flex: 1 }}>
            New Custom Form
          </Typography>
          <Chip label="Step 1 of 2: Configure" size="small" sx={{ bgcolor: themeColors.infoLight, color: themeColors.primary, fontWeight: 600 }} />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: themeColors.background, p: 4 }}>
          <Paper elevation={0} sx={{ maxWidth: 520, width: '100%', p: 4, borderRadius: '16px', border: `1px solid ${themeColors.border}` }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: themeColors.infoLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <OrgIcon sx={{ fontSize: 22, color: themeColors.primary }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: themeColors.text.primary }}>
                  Assign to Organization
                </Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
                  All leads from this form will be routed to the selected org and LOB
                </Typography>
              </Box>
            </Box>

            <Stack spacing={2.5}>
              <FormControl fullWidth size="small" sx={selectSx} required disabled={!isSuperAdmin}>
                <InputLabel sx={{ fontSize: '13px' }}>Organization *</InputLabel>
                <Select label="Organization *" value={orgId} onChange={(e) => handleOrgChange(e.target.value)} sx={{ fontSize: '13px' }}>
                  {isSuperAdmin
                    ? orgs.map(o => <MenuItem key={o.id} value={o.id} sx={{ fontSize: '13px' }}>{o.name}</MenuItem>)
                    : <MenuItem value={userOrgId} sx={{ fontSize: '13px' }}>{userOrgName || 'Your Organization'}</MenuItem>}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={selectSx} required disabled={!orgId}>
                <InputLabel sx={{ fontSize: '13px' }}>Line of Business *</InputLabel>
                <Select label="Line of Business *" value={lobId} onChange={(e) => handleLobChange(e.target.value)} sx={{ fontSize: '13px' }}>
                  {lobs.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '13px' }}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>

              {configComplete && (
                <Alert severity="success" sx={{ fontSize: '12px', borderRadius: '8px' }}>
                  Leads will be assigned to <strong>{orgName}</strong> › <strong>{lobName}</strong>
                </Alert>
              )}

              <Alert severity="info" sx={{ fontSize: '12px', borderRadius: '8px', bgcolor: themeColors.infoLight, border: `1px solid ${themeColors.primary}30` }}>
                These values are <strong>internal only</strong> — visitors filling out the form will never see or enter them.
              </Alert>

              <Button
                variant="contained" size="large" fullWidth
                endIcon={<NextIcon />}
                disabled={!configComplete}
                onClick={() => setStep('builder')}
                sx={{
                  mt: 1, py: 1.5, fontSize: '14px', fontWeight: 700,
                  background: configComplete ? themeColors.primary : undefined,
                  '&:hover': { background: themeColors.primaryDark },
                  boxShadow: configComplete ? '0 2px 12px rgba(75,48,130,0.3)' : 'none',
                }}
              >
                Continue to Form Builder
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Box>
    )
  }

  // ── STEP 2: Form builder ───────────────────────────────────────────────────
  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Top bar */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${themeColors.border}`, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => navigate('/custom-forms')} sx={{ color: themeColors.text.secondary }}>
          <BackIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Enter form name..."
            variant="standard"
            sx={{
              '& .MuiInput-input': { fontSize: '16px', fontWeight: 700, color: themeColors.text.primary },
              '& .MuiInput-underline:before': { borderBottom: '1px dashed #ccc' },
            }}
          />
          {slug && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>{formUrl}</Typography>
              <Tooltip title="Copy URL">
                <IconButton size="small" onClick={copyUrl} sx={{ p: 0.25, color: themeColors.text.muted }}>
                  <CopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open form">
                <IconButton size="small" onClick={() => window.open(formUrl, '_blank')} sx={{ p: 0.25, color: themeColors.text.muted }}>
                  <OpenIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Org › LOB breadcrumb */}
        {configComplete && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Chip label={orgName} size="small" sx={{ fontSize: '11px', bgcolor: '#ede9f7', color: themeColors.primary }} />
            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>›</Typography>
            <Chip label={lobName} size="small" sx={{ fontSize: '11px', bgcolor: '#ede9f7', color: themeColors.primary }} />
          </Box>
        )}

        {/* Back to step 1 (new forms only) */}
        {!isEdit && (
          <Tooltip title="Change org / LOB">
            <Button size="small" variant="outlined" onClick={() => setStep('config')}
              sx={{ fontSize: '12px', borderColor: themeColors.border, color: themeColors.text.secondary, flexShrink: 0 }}>
              ← Step 1
            </Button>
          </Tooltip>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={status === 'Active'}
              onChange={(e) => setStatus(e.target.checked ? 'Active' : 'Inactive')}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: themeColors.success },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: themeColors.success },
              }}
            />
          }
          label={
            <Chip label={status} size="small" sx={{
              fontSize: '11px',
              bgcolor: status === 'Active' ? themeColors.successLight : '#f3f2f2',
              color:   status === 'Active' ? themeColors.success       : themeColors.text.secondary,
            }} />
          }
        />

        <Button
          variant="contained" size="small"
          startIcon={saveMutation.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !formName.trim() || !configComplete}
          sx={{
            fontSize: '13px', fontWeight: 600,
            background: themeColors.primary,
            '&:hover': { background: themeColors.primaryDark },
            boxShadow: '0 2px 8px rgba(75,48,130,0.3)',
          }}
        >
          {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Form'}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, bgcolor: '#fff', borderBottom: `1px solid ${themeColors.border}`, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 40, '& .MuiTab-root': { fontSize: '13px', minHeight: 40, fontWeight: 500 } }}
          TabIndicatorProps={{ sx: { bgcolor: themeColors.primary } }}
        >
          <Tab label="Fields" />
          <Tab label="Settings" />
          {isEdit && <Tab label="Responses" />}
        </Tabs>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── FIELDS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>

            {/* Left: field editor list */}
            <Box sx={{ width: '50%', borderRight: `1px solid ${themeColors.border}`, overflow: 'auto', p: 2, bgcolor: '#fff' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: themeColors.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Form Fields ({fields.filter(f => f.isVisible).length} visible)
              </Typography>
              <Stack spacing={1}>
                {fields.map((field, idx) => (
                  <Box
                    key={field.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <FieldEditor
                      field={field} index={idx}
                      onUpdate={handleUpdateField}
                      onDelete={handleDeleteField}
                      dragHandleProps={{ onMouseDown: (e: any) => e.stopPropagation() }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Right: live preview */}
            <Box sx={{ width: '50%', overflow: 'auto', p: 3, bgcolor: themeColors.background }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: themeColors.text.secondary, mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Live Preview
              </Typography>
              <FormPreview
                formName={formName || 'My Lead Form'}
                description={description}
                successMessage={successMessage}
                fields={fields}
                mode="desktop"
                formCompanyName={formCompanyName || undefined}
                formLogoUrl={formLogoUrl || undefined}
              />
            </Box>
          </Box>
        )}

        {/* ── RESPONSES TAB ──────────────────────────────────────────────── */}
        {activeTab === 2 && isEdit && (
          <Box sx={{ flex: 1, overflow: 'auto', bgcolor: themeColors.background }}>
            <LeadFormResponses formId={id} embedded />
          </Box>
        )}

        {/* ── SETTINGS TAB ───────────────────────────────────────────────── */}
        {activeTab === 1 && (
          <Box sx={{ flex: 1, p: 3, overflow: 'auto', bgcolor: '#fff' }}>
            <Box sx={{ maxWidth: 680, mx: 'auto' }}>
              <Stack spacing={3}>

                {/* Basic info */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 2 }}>Basic Information</Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField label="Form Name" value={formName} onChange={(e) => setFormName(e.target.value)} required fullWidth size="small" sx={inputSx} />
                      <TextField
                        label="Form Slug (URL)" value={slug}
                        onChange={(e) => { setSlug(slugify(e.target.value)); setSlugManual(true) }}
                        fullWidth size="small" helperText={`/forms/${slug}${companyUrlSegment}`} sx={inputSx}
                        InputProps={{ endAdornment: (
                          <Tooltip title="Copy URL">
                            <IconButton size="small" onClick={copyUrl} edge="end">
                              <CopyIcon sx={{ fontSize: 16, color: themeColors.text.muted }} />
                            </IconButton>
                          </Tooltip>
                        )}}
                      />
                    </Box>
                    <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={2} fullWidth size="small" sx={inputSx} />
                  </Stack>
                </Box>

                <Divider />

                {/* Branding */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>Branding</Typography>
                  <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mb: 2 }}>
                    Shown at the top of the public form. Both fields are optional.
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Company Name (optional)"
                      value={formCompanyName}
                      onChange={(e) => setFormCompanyName(e.target.value)}
                      fullWidth size="small" sx={inputSx}
                      placeholder="e.g. Acme Corp"
                      inputProps={{ maxLength: 200 }}
                    />

                    {/* Logo upload */}
                    <Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: themeColors.text.primary, mb: 1 }}>
                        Company Logo (optional)
                      </Typography>
                      {formLogoUrl ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={formLogoUrl}
                            alt="Logo preview"
                            sx={{
                              height: 48, maxWidth: 160, objectFit: 'contain',
                              borderRadius: '8px', border: `1px solid ${themeColors.border}`,
                              bgcolor: '#fafafa', p: '6px',
                            }}
                          />
                          <Button
                            size="small" variant="outlined" color="error"
                            startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setFormLogoUrl('')}
                            sx={{ fontSize: '12px', borderRadius: '6px' }}
                          >
                            Remove
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          disabled={logoUploading}
                          startIcon={logoUploading
                            ? <CircularProgress size={14} sx={{ color: themeColors.primary }} />
                            : <UploadIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            fontSize: '13px', borderColor: themeColors.border,
                            color: themeColors.text.secondary, borderRadius: '8px',
                            '&:hover': { borderColor: themeColors.primary, color: themeColors.primary },
                          }}
                        >
                          {logoUploading ? 'Uploading…' : 'Upload Logo'}
                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={async (e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              if (file.size > 1_048_576) {
                                toast.error('Logo must be 1 MB or smaller')
                                return
                              }
                              setLogoUploading(true)
                              try {
                                const url = await leadFormsApi.uploadLogo(file)
                                setFormLogoUrl(url)
                              } catch {
                                toast.error('Logo upload failed. Please try again.')
                              } finally {
                                setLogoUploading(false)
                                // reset input so the same file can be re-selected
                                e.target.value = ''
                              }
                            }}
                          />
                        </Button>
                      )}
                      <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mt: 0.75 }}>
                        PNG, JPG, SVG or WebP · Max 512 KB · Shown to the left of the company name
                      </Typography>
                    </Box>

                    {/* Live header preview */}
                    {(formCompanyName || formLogoUrl) && (
                      <Box>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: themeColors.text.secondary, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Header Preview
                        </Typography>
                        <Box
                          sx={{
                            background: themeColors.primaryGradient,
                            borderRadius: '10px',
                            px: 2.5, py: 2,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {formLogoUrl && (
                              <Box
                                component="img"
                                src={formLogoUrl}
                                alt="Logo"
                                sx={{
                                  width: 44, height: 44, borderRadius: '10px',
                                  objectFit: 'contain',
                                  bgcolor: 'rgba(255,255,255,0.15)',
                                  p: '5px',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Box>
                              {formCompanyName && (
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                                  {formCompanyName}
                                </Typography>
                              )}
                              {formName && (
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, mt: formCompanyName ? 0.25 : 0 }}>
                                  {formName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Internal assignment */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>Internal Assignment</Typography>
                  <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mb: 2 }}>
                    These values are <strong>invisible to form visitors</strong>. Every lead submitted through this form will automatically inherit the selected organization, LOB, and warehouse.
                  </Typography>

                  {!configComplete && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: '12px' }}>
                      All three fields are required. The form cannot be saved or published until they are all selected.
                    </Alert>
                  )}

                  <Stack spacing={2}>
                    <FormControl fullWidth size="small" sx={selectSx} required disabled={!isSuperAdmin}>
                      <InputLabel sx={{ fontSize: '13px' }}>Organization *</InputLabel>
                      <Select label="Organization *" value={orgId} onChange={(e) => handleOrgChange(e.target.value)} sx={{ fontSize: '13px' }}>
                        {isSuperAdmin
                          ? orgs.map(o => <MenuItem key={o.id} value={o.id} sx={{ fontSize: '13px' }}>{o.name}</MenuItem>)
                          : <MenuItem value={userOrgId} sx={{ fontSize: '13px' }}>{userOrgName || 'Your Organization'}</MenuItem>}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={selectSx} required disabled={!orgId}>
                      <InputLabel sx={{ fontSize: '13px' }}>Line of Business *</InputLabel>
                      <Select label="Line of Business *" value={lobId} onChange={(e) => handleLobChange(e.target.value)} sx={{ fontSize: '13px' }}>
                        {lobs.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '13px' }}>{l.name}</MenuItem>)}
                      </Select>
                    </FormControl>

                    {/* TODO: Warehouse dropdown — warehouseId state & warehouses fetch not yet implemented
                    <FormControl fullWidth size="small" sx={selectSx} required disabled={!lobId}>
                      <InputLabel sx={{ fontSize: '13px' }}>Warehouse *</InputLabel>
                      <Select label="Warehouse *" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} sx={{ fontSize: '13px' }}>
                        {warehouses.map(w => <MenuItem key={w.id} value={w.id} sx={{ fontSize: '13px' }}>{w.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    */}

                    {configComplete && (
                      <Alert severity="success" sx={{ fontSize: '12px', borderRadius: '8px' }}>
                        Leads will be assigned to <strong>{orgName}</strong> › <strong>{lobName}</strong>{/* › <strong>{warehouseName}</strong> — TODO: warehouseName not yet declared */}
                      </Alert>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Source Tracking */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>Source Tracking</Typography>
                  <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mb: 2 }}>
                    These values are set here and applied <strong>automatically to every lead</strong> created by this form — visitors never see or enter them.
                  </Typography>
                  <Stack spacing={2}>
                    {/* Source is always custom_form — readonly */}
                    <TextField
                      label="Source"
                      value="custom_form"
                      InputProps={{ readOnly: true }}
                      fullWidth size="small" sx={inputSx}
                      helperText="Always set to 'custom_form' for leads from this form"
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                      <TextField
                        label="Source Ad Name"
                        value={sourceAdName}
                        onChange={(e) => setSourceAdName(e.target.value)}
                        fullWidth size="small" sx={inputSx}
                        placeholder="e.g. Summer_Banner"
                      />
                      <TextField
                        label="Source Campaign"
                        value={sourceCampaign}
                        onChange={(e) => setSourceCampaign(e.target.value)}
                        fullWidth size="small" sx={inputSx}
                        placeholder="e.g. Q3_2024"
                      />
                      <TextField
                        label="Source Medium"
                        value={sourceMedium}
                        onChange={(e) => setSourceMedium(e.target.value)}
                        fullWidth size="small" sx={inputSx}
                        placeholder="e.g. instagram"
                      />
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* After submission */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 2 }}>After Submission</Typography>
                  <Stack spacing={2}>
                    <TextField label="Success Message" value={successMessage} onChange={(e) => setSuccessMessage(e.target.value)} multiline rows={2} fullWidth size="small" sx={inputSx} />
                    <TextField
                      label="Redirect URL (optional)" value={redirectUrl}
                      onChange={(e) => setRedirectUrl(e.target.value)}
                      fullWidth size="small" sx={inputSx}
                      placeholder="https://yoursite.com/thank-you"
                      helperText="If set, visitor will be redirected here after submission"
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* Share */}
                <Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: themeColors.text.primary, mb: 2 }}>Share This Form</Typography>
                  <Box sx={{ p: 2, bgcolor: '#f8f7fc', borderRadius: '8px', border: `1px solid ${themeColors.border}` }}>
                    <Stack spacing={1.5}>
                      {[
                        { label: 'Direct Link',        value: formUrl },
                        { label: 'Instagram Bio',      value: formUrl },
                        { label: 'Facebook Campaign',  value: formUrl },
                        { label: 'WhatsApp',           value: `https://wa.me/?text=${encodeURIComponent(`Fill our form: ${formUrl}`)}` },
                        { label: 'Website Embed',      value: `<iframe src="${formUrl}" width="100%" height="600" frameborder="0"></iframe>` },
                      ].map(item => (
                        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: themeColors.text.primary }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.value}
                            </Typography>
                          </Box>
                          <Tooltip title="Copy">
                            <IconButton size="small"
                              onClick={() => { navigator.clipboard.writeText(item.value); toast.success(`${item.label} copied!`) }}
                              sx={{ color: themeColors.primary, border: `1px solid ${themeColors.primary}40`, borderRadius: '6px' }}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>

              </Stack>
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  )
}
